import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from "react";
import { isMobile, isTablet } from "react-device-detect";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AppNotificationTypeEnum } from "../../contexts/app-notification-context/AppNotificationContextTypes";
import { useAppNotificationContext } from "../../contexts/app-notification-context/UseAppNotificationContext";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { useAttributeSchema } from "../../queries/attributes/UseAttributeSchema";
import { useObjectsAttributeValues } from "../../queries/attributes/UseObjectsAttributeValues";
import { useOrganizations } from "../../queries/organizations/UseOrganizations";
import AttributeDataService from "../../services/attribute-data-service/AttributeDataService";
import { defaultAttributeSchema, AttributeSchemaTypeEnum, IAttributeSchemaModel, IObjectAttributes } from "../../services/attribute-data-service/Models";
import { RestError } from "../../services/rest-service/RestService";
import { ActionMenuItem, BaseActions } from "../action-menu/ActionMenuItem";
import { ViewOptionStateEnum } from "../contextual-app-bar/ViewOptionStateEnum";
import LoadingZone from "../loading-zone/LoadingZone";
import StepMenu, { StepDefinition } from "../step-menu/StepMenu";
import { useStepMenuState, StepMenuEditModeEnum } from "../step-menu/StepMenuState";
import MobileStepMenu from "../step-menu/mobile/MobileStepMenu";
import UnsavedChangesPrompt from "../unsaved-changes-prompt/UnsavedChangesPrompt";
import AttributeSchemaEditorUtils, { AttributeSchemaAdminMode, AttributeSchemaFormType, StepEnum, AttributeOptionActionEnum, AttributeSchemaUsageActions, usageActionItems } from "./AttributeSchemaEditorUtils";
import { useAttributeSchemaObjectState } from "./attribute-schema-objects/AttributeSchemaObjectState";
import AttributeSchemaObjects, { AttributeSchemaObjectsType } from "./attribute-schema-objects/AttributeSchemaObjects";
import AttributeSchemaProperties, { AttributeSchemePropertiesType } from "./attribute-schema-properties/AttributeSchemaProperties";
import AttributeSchemaSummary from "./attribute-schema-summary/AttributeSchemaSummary";
import Form from "../forms/form/Form";
import { IeLynxObject } from "../../common/models/eLynxObject";

export type AttributeSchemaEditorCloseType = {
    reason: "cancel" | "save";
    attributeSchemaID: number | undefined;
};

export type AttributeSchemaEditorProps = {
    organizationID: number,
    groupID: number,
    attributeSchemaID: number;
    attributeSchemaName: string;
    targetObject?: IeLynxObject
    onClose: ({ reason, attributeSchemaID }: AttributeSchemaEditorCloseType) => void;
};

export default function AttributeSchemaEditor({
    organizationID,    
    groupID,
    attributeSchemaID,
    attributeSchemaName,
    targetObject,
    onClose,
}: AttributeSchemaEditorProps): JSX.Element {

    const isPhone = isMobile && !isTablet;
    const propertiesRef = useRef<AttributeSchemePropertiesType>(undefined);
    const objectsRef = useRef<AttributeSchemaObjectsType>(undefined);

    const { confirm
        , acknowledge 
    } = useConfirmation();
    const { t } = useTranslation();
    const [viewOptionState, setViewOptionState] = useState(ViewOptionStateEnum.Default);
    const [openSideSheet, setOpenSideSheet] = useState(isPhone ? false: true);
    const [,setAppNotificationState] = useAppNotificationContext();
    
    const attributeSchemaObjectState = useAttributeSchemaObjectState();

    
    const mode = attributeSchemaName === '' ?     
        AttributeSchemaAdminMode.Add : 
        AttributeSchemaAdminMode.Edit;

    const {data, status , refetch: refetchSchema } = useQueryNotifications(useAttributeSchema)({
        groupID,
        organizationID: organizationID,
        id: attributeSchemaID,
        name: attributeSchemaName
    });


    const {data: objectAttributeValues, status: objectStatus, refetch: refetchValues } = useQueryNotifications(useObjectsAttributeValues)({
        groupID,
        organizationID: organizationID,
        attributeName: data?.name ?? '',
        enabled: data !== undefined
    });


    const {data: organizations} = useQueryNotifications(useOrganizations)({
    });

    const organization = organizations?.find(o => o.id === organizationID);

    const form = useForm<AttributeSchemaFormType>({
        mode: 'onChange',
        defaultValues: {...defaultAttributeSchema,
            includeDevices: false,
            includeTankBatteries: false,
            includeTanks: false,
            includeWells: false,
            devices: [],
            wells: [],
            tanks:[],
            tankBatteries: [],
        }
    });

    const {
        getValues,
        watch,
        formState: {
            //dirtyFields,
            isDirty,
            isValid,
            //errors
            //defaultValues
            //touchedFields
        },
        reset,
        //setValue,
        control,
        handleSubmit,
    } = form;

    const hasChanges = isDirty && isValid;

    //console.log(`hasChanges: ${hasChanges}`)

    const formData = useWatch({ control: control }) as AttributeSchemaFormType;

    const [name,
        schemaType,
        valueOptions,
        includeDevices,
        includeTanks,
        includeWells,
        includeTankBatteries
    ] = watch([
        'name',
        'schemaType',
        'valueOptions',
        'includeDevices',
        'includeTanks',
        'includeWells',
        'includeTankBatteries'
    ]);


    const includeObjects = includeDevices || includeTanks || includeWells || includeTankBatteries;

    const propertiesValid = isValid && includeObjects && ((schemaType === AttributeSchemaTypeEnum.PredefinedValue || 
                            schemaType === AttributeSchemaTypeEnum.PredefinedValueAndFreeformText) ?
                                valueOptions.length > 0 && 
                            valueOptions.find(o => o.value.trim() !== '') !== undefined &&
                            name !== '' :
                            name !== '' && 
                            schemaType !== AttributeSchemaTypeEnum.None);
    const stepDefinitions: StepDefinition[] = useMemo(() => {

        const defs = [{
            key: StepEnum.Properties,
            title: t("attributeSchema.steps.properties.title"),
            valid: propertiesValid

        }, {
            key: StepEnum.Usage,
            title: t("attributeSchema.steps.usage.title"),
            disabled: !propertiesValid,
            valid: true
        },{
            key: StepEnum.Summary,
            title: t("attributeSchema.steps.summary.title"),
            disabled: !propertiesValid,
        }];


        return targetObject !== undefined ? defs.filter(o => o.key !== StepEnum.Usage) : defs;
    }, [
        t,
        propertiesValid,
        targetObject
    ]);

    const {
        state: {
            landing,
            steps,
            activeStep,
            cancelButtonState,
            prevButtonState,
            nextButtonState,
            saveChangesButtonState,
        },
        setActiveStep,
        setNextButtonDisabled,
        setSaveChangesButtonLabel,
        setSaveChangesButtonDisabled,
        setSaveChangesButtonVisible,
    } = useStepMenuState(
        StepMenuEditModeEnum.Create,
        undefined,
        stepDefinitions
    );

    const updateButtonStates = useCallback(() => {

         switch (activeStep) {
            case StepEnum.Properties:
                setSaveChangesButtonLabel(t(mode === AttributeSchemaAdminMode.Add ? 
                        'attributeSchema.create' : 
                        'attributeSchema.save'));
                setNextButtonDisabled(!propertiesValid);
                break;

            case StepEnum.Usage:
                setNextButtonDisabled(false);
                break;

            case StepEnum.Summary:
                setSaveChangesButtonVisible(true);
                setSaveChangesButtonDisabled(!hasChanges);
                break;

            default:
                break;
        }
    }, [
        t,
        mode,
        setNextButtonDisabled,
        setSaveChangesButtonLabel,
        setSaveChangesButtonDisabled,
        setSaveChangesButtonVisible,
        activeStep,
        propertiesValid,
        hasChanges
    ]);


    async function onCancelEdit() {

        if (!hasChanges || (await getUserExitConfirmation())) {
            onClose({ 
                reason: "cancel", 
                attributeSchemaID: formData.id
            });
        }
    }

    function onCancelStep() {
        onCancelEdit();
    }

    function onStepChange(_fromStep: number, toStep: number) {

        setActiveStep(toStep);
    }

    async function onSaveChanges() {

        try {

            const originalModel = data as IAttributeSchemaModel;
            const formValues = getValues();
            const model = AttributeSchemaEditorUtils.mapToModel(originalModel, formValues);

            const counts = AttributeSchemaEditorUtils.getUsageCounts(formData);
            const totalCount = counts.added + counts.deleted + counts.updated;

            if (AttributeSchemaEditorUtils.schemaChanged(model, originalModel)) {

                const schema = await AttributeDataService.saveAttributeSchema(organizationID, groupID, model);

                model.id = schema.id;
                formValues.id = schema.id;

                if (totalCount === 0) {
                    acknowledge(t("attributeSchema.saveSchemaOnlySuccess", {
                        name: schema.name
                    }));

                    onClose({
                        reason: "save",
                        attributeSchemaID: schema.id
                    });
                } 
            }

            const attributes = AttributeSchemaEditorUtils.mapToAttributesModel(objectAttributeValues as IObjectAttributes, formValues);

            if (attributes.length) {
                await AttributeDataService.saveAttributes(organizationID, groupID, attributes);

                acknowledge(
                    t("attributeSchema.saveSuccess", {
                    name: model.name,
                    count: totalCount
                }));

                onClose({
                    reason: "save",
                    attributeSchemaID: model.id
                });

            }

            if (objectAttributeValues) {

                const formAttributes = formValues.devices
                                .concat(objectAttributeValues.tanks)
                                .concat(objectAttributeValues.tankBatteries)
                                .concat(objectAttributeValues.wells);

                const originalAttributes = objectAttributeValues.devices
                                .concat(objectAttributeValues.tanks)
                                .concat(objectAttributeValues.tankBatteries)
                                .concat(objectAttributeValues.wells);
                if (originalModel.id > 0 && 
                        originalAttributes.filter(o => o.selected === true).length > 0 && 
                        formAttributes.filter(o => o.selected === true).length === 0) {

                    await confirm({
                        dialogTitle: t("attributeSchema.remove.title"),
                        confirmationText: t("attributeSchema.remove.text"),
                        yesButtonText: t("buttons.okay"),
                        showConfirmationText: true,
                        showYesButton: true,
                        showNoButton: false,
                        showCloseButton: true
                    });

                }
            }
        } catch (error) {
            const restError = error as RestError;
        
            setAppNotificationState({
                notificationType: AppNotificationTypeEnum.Error,
                show: true,
                error: restError
            });

        } finally {
            await Promise.all([refetchSchema(), refetchValues()]);
        }
    }

    function onExecuteAction(menuItem: ActionMenuItem) {

        switch(activeStep) {
            case StepEnum.Properties: {
                const action = menuItem.action as AttributeOptionActionEnum | BaseActions;
                propertiesRef.current?.executeAction(action);
            }
                break;

            case StepEnum.Usage: {
                const action = menuItem.action as AttributeSchemaUsageActions | BaseActions;
                objectsRef.current?.executeAction(action);
            }
                break;

            default:
                break;
        }
    }

    async function getUserExitConfirmation() {

        return (await confirm({
            dialogTitle: t("cancelConfirmation.title"),
            confirmationText: t("cancelConfirmation.confirmationText"),
            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true,
            showCloseButton: true
        })) === "yes";
    }

    useEffect(() => {

        if (data && objectAttributeValues && organization) {

            //console.log('resetting form')
            reset(AttributeSchemaEditorUtils.mapToFormValues(data, objectAttributeValues, organization));
        }
    }, [
        data,
        objectAttributeValues,
        organization,
        reset,
    ]);

    useEffect(() => {

         updateButtonStates();
    }, [
        formData,
        updateButtonStates
    ]);

    useEffect(() => {
        if (isPhone && activeStep === StepEnum.Usage) {
            setOpenSideSheet(false);
        }
    }, [
        isPhone,
        activeStep,
        setOpenSideSheet
    ]);
    

    return (
        <LoadingZone isLoading={status === 'loading' || objectStatus === 'loading'}>
            
            {data && objectAttributeValues && <Form form={form} sx={{ padding: 0 }}>

                {isPhone && 
                <MobileStepMenu 
                    steps={steps}
                    title={mode === AttributeSchemaAdminMode.Add ? 'New Attribute': formData.name}
                    activeStep={activeStep}
                    cancelButtonState={cancelButtonState}
                    prevButtonState={prevButtonState}
                    nextButtonState={nextButtonState}
                    saveChangesButtonState={saveChangesButtonState}
                    onCancel={onCancelEdit}
                    onSaveChanges={handleSubmit(() => onSaveChanges())}
                    onCancelStep={onCancelStep}
                    onStepChange={onStepChange}
                    onStepClick={step => setActiveStep(step.key)}
                    viewOptionState={viewOptionState}
                    setViewOptionState={setViewOptionState}
                    onToggleOptionsAction={activeStep === StepEnum.Usage ? () => setOpenSideSheet(!openSideSheet) : undefined}
                    // actions={activeStep === StepEnum.Properties && 
                    //             (schemaType === AttributeSchemaTypeEnum.PredefinedValue || 
                    //              schemaType === AttributeSchemaTypeEnum.PredefinedValueAndFreeformText) ? 
                    //             attributeOptionActionItems :
                    //         activeStep === StepEnum.Usage ?
                    //             usageActionItems :
                    //         undefined
                    actions={
                    activeStep === StepEnum.Usage ?
                        usageActionItems :
                    undefined

                    }
                    onExecuteAction={onExecuteAction}
                >
                    {
                        activeStep === StepEnum.Properties &&
                        <AttributeSchemaProperties 
                            organizationID={organizationID}
                            targetObject={targetObject}
                            ref={propertiesRef} />
                    }

                    {activeStep === StepEnum.Usage &&
                    
                        <AttributeSchemaObjects schemaUsageState={attributeSchemaObjectState} 
                            openSideSheet={openSideSheet} 
                            onCloseSideSheet={() => setOpenSideSheet(false)}
                            setViewOptionState={setViewOptionState}
                            ref={objectsRef}
                        />
                    }


                    {activeStep === StepEnum.Summary &&
                        <AttributeSchemaSummary />
                    }

                </MobileStepMenu>
                }


                {
                !isPhone && <StepMenu
                    landing={landing}
                    steps={steps}
                    title={mode === AttributeSchemaAdminMode.Add ? 'New Attrbute': formData.name}
                    nonLinear={true}
                    activeStep={activeStep}
                    cancelButtonState={cancelButtonState}
                    prevButtonState={prevButtonState}
                    nextButtonState={nextButtonState}
                    saveChangesButtonState={saveChangesButtonState}
                    onCancel={onCancelEdit}
                    onSaveChanges={handleSubmit(() => onSaveChanges())}
                    onCancelStep={onCancelStep}
                    onStepChange={onStepChange}
                    onStepClick={step => setActiveStep(step.key)}
                >
                    {activeStep === StepEnum.Properties &&
                        <AttributeSchemaProperties 
                            ref={propertiesRef}  
                            organizationID={organizationID}                            
                            targetObject={targetObject}
                            />
                    }

                    {activeStep === StepEnum.Usage &&
                        <AttributeSchemaObjects 
                            schemaUsageState={attributeSchemaObjectState} 
                            openSideSheet={openSideSheet}
                            onCloseSideSheet={() => setOpenSideSheet(false)}
                            ref={objectsRef}
                        />
                    }

                    {activeStep === StepEnum.Summary &&
                        <AttributeSchemaSummary />
                    }

                </StepMenu>
                }
            </Form>
            }


            <UnsavedChangesPrompt
                hasChanges={hasChanges}
                getUserConfirmation={getUserExitConfirmation}
            />
        </LoadingZone>
    );
}
